import { useEffect, useRef, useState } from 'preact/hooks';
import { loadItem, type ChangeRow, type Cite, type ItemDoc } from '../data';
import { CHANGES, fmtLong, isTrialVote, otherChamber, outcomeWords, TRIAL_NOTE, unexpectedLabel } from '../format';
import { defFor, JARGON, NO_ROLL_CALL, NO_ROLL_CALL_ANY } from '../grid/marks';
import { navigate, stepTo } from '../router';
import { ShareButton, SharePanel, itemShare, pageTitle } from './Share';
import './overlay.css';

// The item modal (README, "Item modal"). Every figure here comes from the item file, which the
// build refuses to write unless the grid's own cells add up to the same tallies. The AI summary
// appears only once the review desk has approved it (D1, D7, T14); until then an item shows the
// CRS summary (T5), or says its summary is not published yet.

type Load<T> = { state: 'loading' } | { state: 'failed'; why: string } | { state: 'loaded'; doc: T };

export function useOverlay(onClose: () => void) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    box.current?.focus();
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('keydown', key); before?.focus?.(); };
  }, []);
  return box;
}

const KIND: Record<string, string> = {
  bill: 'Bill', joint_resolution: 'Joint resolution', concurrent_resolution: 'Concurrent resolution',
  nomination: 'Nomination', nomination_batch: 'Nominations confirmed together',
};

export function kindLabel(doc: { kind: string; number: string }): string {
  if (doc.kind === 'simple_resolution') return doc.number.startsWith('H.') ? 'House resolution' : 'Senate resolution';
  return KIND[doc.kind] ?? doc.kind;
}

function presidentLine(doc: ItemDoc): string {
  const p = doc.president, when = p.date ? ' ' + fmtLong(p.date) : '';
  switch (p.status) {
    case 'signed': return `Signed${when}` + (doc.law_number ? ` · ${doc.law_number}` : '');
    case 'vetoed':
      return `Vetoed${when}` + (doc.outcome === 'became_law' ? ' · Congress overrode the veto' : '')
        + (doc.law_number ? ` · ${doc.law_number}` : '');
    case 'without_signature':
      return `Became law without signature${p.date ? ', ' + fmtLong(p.date) : ''}` + (doc.law_number ? ` · ${doc.law_number}` : '');
    case 'pending': return 'Passed both chambers; awaiting the President’s action.';
    case 'not_yet': return doc.outcome === 'passed_different'
      ? 'Not before the President yet: the House and Senate passed different versions.'
      : 'Not before the President yet: both chambers must pass it first.';
    case 'unknown': return 'Became law; the record names no signature or veto.';
  }
  if (doc.kind === 'nomination' || doc.kind === 'nomination_batch') return 'Nominations do not return to the President for signature.';
  if (doc.kind === 'simple_resolution' || doc.kind === 'concurrent_resolution') return 'Does not go to the President.';
  if (/amendment to the constitution/i.test(doc.official_title ?? '')) return 'A constitutional amendment does not go to the President.';
  return 'Not presented to the President.';
}

export const num = (n: number | null) => (n == null ? '—' : String(n));
const tally = (v: { yea: number | null; nay: number | null; present: number | null; not_voting: number | null }) =>
  `${num(v.yea)}–${num(v.nay)}` + (v.present ? `, ${v.present} present` : '') + (v.not_voting ? `, ${v.not_voting} not voting` : '');

export function ItemModal({ id, onClose }: { id: string; onClose: () => void }) {
  const [load, setLoad] = useState<Load<ItemDoc>>({ state: 'loading' });
  const [procOpen, setProcOpen] = useState(false);
  const [titlesOpen, setTitlesOpen] = useState(false);
  const box = useOverlay(onClose);

  useEffect(() => {
    setLoad({ state: 'loading' }); setProcOpen(false); setTitlesOpen(false);
    loadItem(id).then((doc) => { if (doc.id !== id) stepTo(`/item/${doc.id}`); setLoad({ state: 'loaded', doc }); })
      .catch((e) => setLoad({ state: 'failed', why: String(e.message ?? e) }));
  }, [id]);
  useEffect(() => { if (load.state !== 'loading') pageTitle(load.state === 'loaded' ? itemShare(load.doc).title : null); }, [load]);

  return (
    <div class="sr-modal-backdrop" onClick={onClose}>
      <div ref={box} class="sr-modal" role="dialog" aria-modal="true" aria-label="Item" tabIndex={-1}
           onClick={(e) => e.stopPropagation()}>
        {/* A phone shows this as a full-screen sheet with a way back at the top (README §11). */}
        <button class="btn btn-ghost sr-sheet-back" onClick={onClose}>← Back</button>
        {load.state === 'loading' && <div class="sr-overlay-state sr-muted">Loading the item…</div>}
        {load.state === 'failed' && (
          <div class="sr-overlay-state">
            <p>This item could not be loaded (<code>{load.why}</code>).</p>
            <button class="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        )}
        {load.state === 'loaded' && <ItemBody doc={load.doc} onClose={onClose}
          procOpen={procOpen} setProcOpen={setProcOpen} titlesOpen={titlesOpen} setTitlesOpen={setTitlesOpen} />}
      </div>
    </div>
  );
}

// Which version of the text a CRS summary describes, from the record's own label.
const STAGE: Record<string, string> = {
  'Introduced in House': 'as introduced in the House',
  'Introduced in Senate': 'as introduced in the Senate',
  'Reported to House': 'as reported to the House by committee',
  'Reported to House, Part I': 'as reported to the House by committee',
  'Reported to Senate': 'as reported to the Senate by committee',
  'Passed House': 'as passed by the House',
  'Passed Senate': 'as passed by the Senate',
  'House agreed to Senate amendment': 'as the House agreed to the Senate’s amendment',
  'Senate agreed to House amendment': 'as the Senate agreed to the House’s amendment',
  'Public Law': 'as enacted',
};
const SHOWN_WORDS = 160;      // the opening shown before "Read the full summary"

/** The Congressional Research Service's summary (decision T5): shown as the CRS's own
 *  work, with the version it describes. Beside an approved AI summary it stays, below it,
 *  until Dan decides otherwise (T5 left that question for when ours switched on). */
export function CrsSummary({ crs, finalDate, kind }: { crs: NonNullable<ItemDoc['crs_summary']>; finalDate: string | null;
  kind: string }) {
  const [open, setOpen] = useState(false);
  let words = 0, cut = crs.blocks.length;
  crs.blocks.forEach((b, i) => {
    if (cut === crs.blocks.length && i >= 2 && words >= SHOWN_WORDS) cut = i;
    words += b.t.split(' ').length;
  });
  const shown = open ? crs.blocks : crs.blocks.slice(0, cut);
  const version = crs.stage ? STAGE[crs.stage] ?? `in the version “${crs.stage}”` : null;
  const earlier = !!(crs.date && finalDate && crs.date < finalDate);
  return (
    <div class="sr-sec">
      <div class="sr-kicker">Summary by the Congressional Research Service</div>
      <div class="sr-crs-about sr-ui">
        {version && <>Describes the {kind === 'bill' ? 'bill' : 'resolution'} {version}{crs.date ? `, ${fmtLong(crs.date)}` : ''}. </>}
        {earlier && <>Written before the final vote, so changes made after it may not be reflected.</>}
      </div>
      <div class="sr-crs">
        {shown.map((b, i) => b.k === 'li'
          ? <div key={i} class="sr-crs-li" style={{ marginLeft: (b.d ?? 1) * 18 + 'px' }}>{b.t}</div>
          : <p key={i} class={b.k === 'h' ? 'sr-crs-h' : ''}>{b.t}</p>)}
      </div>
      {cut < crs.blocks.length && (
        <button class="sr-disclose" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? '▾ Show less' : `▸ Read the full summary (${crs.words.toLocaleString('en-US')} words)`}
        </button>
      )}
      <div class="sr-crs-credit sr-ui">
        Written by the Congressional Research Service of the Library of Congress, not by Votes on Record.
      </div>
    </div>
  );
}

// Which version of the text an AI summary describes, from Congress.gov's own version names.
const VERSION: Record<string, string> = {
  'Enrolled Bill': 'as passed by both chambers (the enrolled text)',
  'Engrossed in House': 'as passed by the House', 'Engrossed in Senate': 'as passed by the Senate',
  'Agreed to House': 'as agreed to by the House', 'Agreed to Senate': 'as agreed to by the Senate',
  'Considered and Passed House': 'as passed by the House', 'Considered and Passed Senate': 'as passed by the Senate',
  'Printed as Passed': 'as passed',
  'Introduced in House': 'as introduced in the House', 'Introduced in Senate': 'as introduced in the Senate',
  'Reported in House': 'as reported in the House by committee', 'Reported to Senate': 'as reported to the Senate by committee',
  'Placed on Calendar Senate': 'as placed on the Senate calendar', 'Received in Senate': 'as received in the Senate',
  'Referred in Senate': 'as referred to committee in the Senate',
};
const MODEL: Record<string, string> = {
  'claude-sonnet-5': 'Claude Sonnet 5, an AI model', 'claude-sonnet-5-5': 'Claude Sonnet 5.5, an AI model',
};
const LONG_LABEL = 14;         // a label longer than this sits above its line, not in the 76px gutter

function CiteLink({ cite }: { cite: Cite }) {
  return cite.href
    ? <a class="sr-sum-cite" href={cite.href} rel="noopener">{cite.label}</a>
    : <span class="sr-sum-cite">{cite.label}</span>;
}

/** The AI summary (Phase 5), shown once the review desk has approved it. The design's two
 *  sections, "What it does" and "Not related to the bill's main subject", each line beside
 *  the provision it came from; the main subject leads, since it is what "not related" is
 *  measured against, and what is not related follows it at once, counted and set apart (Dan,
 *  24 Sep 2026: the design put it last, where it was missed). */
// From this many provisions not related to the main subject, the box shows the first, the total
// in its heading, and "Show all" (Dan, 24 Sep 2026: H.R. 8800's list had run to 121 lines).
const UNRELATED_FOLD = 3;

type Summary = NonNullable<ItemDoc['summary']>;
type Listing = NonNullable<Summary['unexpected']>[number]['items'][number];

/** One Unexpected Item: its section beside what it does, its heading above, its dollar figure after,
 *  and the section's own words behind "Quote", as the brief shows a listing. */
function UnexpectedRow({ x, chamber }: { x: Listing; chamber?: string | null }) {
  const [quote, setQuote] = useState(false);
  return (
    <div class={'sr-sum-row' + (x.cite.label.length > LONG_LABEL ? ' sr-sum-long' : '')}>
      <CiteLink cite={x.cite} />
      <span class="sr-sum-text">
        {x.added_after && chamber && <span class="sr-sum-tag sr-ui">{CHANGES.added(chamber)}</span>}
        {x.heading && <span class="sr-sum-heading sr-ui">{x.heading}</span>}
        {x.effect}{' '}
        <button class="sr-disclose sr-sum-quote-btn" onClick={() => setQuote(!quote)} aria-expanded={quote}>
          {quote ? '▾ Quote' : '▸ Quote'}
        </button>
        {quote && <blockquote class="sr-sum-quote">{x.quote}</blockquote>}
      </span>
      {x.amount && <span class="sr-sum-amount">{x.amount}</span>}
    </div>
  );
}

/** Unexpected Items (T18), in the place "Not related" had: grouped by kind in the brief's order, each
 *  group counted, and a long list folded to each kind's first listing and "Show all" (Dan's rule of
 *  24 Sep, kept). The brief's one-line note says what the list is and is not. */
function Unexpected({ s, word, chamber }: { s: Summary; word: string; chamber?: string | null }) {
  const [all, setAll] = useState(false);
  const groups = s.unexpected ?? [];
  const n = groups.reduce((t, g) => t + g.items.length, 0);
  const folded = n >= UNRELATED_FOLD && !all;
  return (
    <div class="sr-sum-unrelated sr-sum-unexpected">
      <div class="sr-kicker">
        Unexpected Items{n > 0 && <span class="sr-sum-count"> · {n} {n === 1 ? 'provision' : 'provisions'}</span>}
      </div>
      {n > 0 && (
        <div class="sr-crs-about sr-ui">
          These provisions fall outside the {word}’s main subject or apply narrowly. This describes the text; it is
          not a judgment.
        </div>
      )}
      {groups.map((g) => (
        <div class="sr-sum-group" key={g.category}>
          <div class="sr-sum-group-head sr-ui">
            {unexpectedLabel(g.category, word)}<span class="sr-sum-group-n"> · {g.items.length}</span>
          </div>
          {(folded ? g.items.slice(0, 1) : g.items).map((x, i) => <UnexpectedRow key={i} x={x} chamber={chamber} />)}
        </div>
      ))}
      {n >= UNRELATED_FOLD && (
        <button class="sr-disclose" onClick={() => setAll(!all)} aria-expanded={all}>
          {all ? '▾ Show less' : `▸ Show all ${n}`}
        </button>
      )}
      {!n && <div class="sr-empty">None found.</div>}
    </div>
  );
}

export function AiSummary({ s, kind, chamber }: { s: Summary; kind: string; chamber?: string | null }) {
  const [allUnrelated, setAllUnrelated] = useState(false);
  const word = kind === 'bill' ? 'bill' : 'resolution';
  const version = s.version.type ? VERSION[s.version.type] ?? `in the version “${s.version.type}”` : null;
  const row = (text: string, cite: Cite, amount?: string | null, key?: number) => (
    <div key={key} class={'sr-sum-row' + (cite.label.length > LONG_LABEL ? ' sr-sum-long' : '')}>
      <CiteLink cite={cite} />
      <span class="sr-sum-text">{text}</span>
      {amount && <span class="sr-sum-amount">{amount}</span>}
    </div>
  );
  const unrelated = s.unrelated ?? [];
  const n = unrelated.length;
  const scan = !!s.unexpected;
  return (
    <>
      <div class="sr-sec">
        <div class="sr-kicker">Main subject</div>
        <div class="sr-crs-about sr-ui">
          {version && <>Describes the {word}{' '}
            {s.version.href ? <a href={s.version.href} rel="noopener">{version}</a> : version}
            {s.version.date ? `, ${fmtLong(s.version.date)}` : ''}. </>}
          {s.version.rule === 'nearest' && <>No version as voted on was printed; this is the latest text printed on or before the final vote. </>}
          {!s.current && <>The text has changed since; a summary of the newer text is not published yet.</>}
        </div>
        <p class="sr-sum-lead">
          {s.main_subject.text}{' '}
          {s.main_subject.cites.map((c, i) => <span key={i}>{i > 0 && ', '}<CiteLink cite={c} /></span>)}
        </p>
        {scan && !!s.core_areas?.length && (
          <div class="sr-crs-about sr-ui">Its core areas: {s.core_areas.join('; ')}.</div>
        )}
        {scan ? <Unexpected s={s} word={word} chamber={chamber} /> : <div class="sr-sum-unrelated">
          <div class="sr-kicker">
            Not related to the {word}’s main subject
            {n > 0 && <span class="sr-sum-count"> · {n} {n === 1 ? 'provision' : 'provisions'}</span>}
          </div>
          {(n >= UNRELATED_FOLD && !allUnrelated ? unrelated.slice(0, 1) : unrelated)
            .map((x, i) => row(x.text, x.cite, x.dollar_amount, i))}
          {n >= UNRELATED_FOLD && (
            <button class="sr-disclose" onClick={() => setAllUnrelated(!allUnrelated)} aria-expanded={allUnrelated}>
              {allUnrelated ? '▾ Show less' : `▸ Show all ${n}`}
            </button>
          )}
          {!n && <div class="sr-empty">None identified.</div>}
        </div>}
      </div>
      <div class="sr-sec">
        <div class="sr-kicker">What it does</div>
        {s.what_it_does.map((x, i) => row(x.text, x.cite, null, i))}
        <div class="sr-crs-credit sr-ui">
          Written automatically from the official text by {MODEL[s.model ?? ''] ?? (s.model || 'an AI model')}
          {scan ? ', in two passes: a summary, then every section read on its own for Unexpected Items' : ''}. Every
          line links to the provision it came from.
          {s.approved_by === 'checks' && s.approved
            ? ` Written ${fmtLong(s.approved)} and published after the automatic checks, without a person reading it first.`
            : s.approved ? ` Approved for publication ${fmtLong(s.approved)}.` : ''}
        </div>
      </div>
    </>
  );
}

type Changes = NonNullable<ItemDoc['changes']>;
const CHANGES_FOLD = 3;          // from this many, a list shows its first and "Show all", as Unexpected Items do

/** One section in one text and not the other: its section (linked in its own text) beside what it does; "in
 *  part" when a section of the other text carries some of it, with what it does not. */
function ChangeRowView({ x, other, missing }: { x: ChangeRow; other: string; missing: string }) {
  return (
    <div class={'sr-sum-row' + (x.cite.label.length > LONG_LABEL ? ' sr-sum-long' : '')}>
      <CiteLink cite={x.cite} />
      <span class="sr-sum-text">
        {x.heading && <span class="sr-sum-heading sr-ui">{x.heading}</span>}
        {x.effect}
        {x.partly && (
          <span class="sr-change-part sr-ui">
            In part: <CiteLink cite={x.partly} /> of the {other} carries some of it.
            {x.part && <> {missing}: {x.part}</>}
          </span>
        )}
      </span>
    </div>
  );
}

function ChangeList({ title, rows, other, missing }: { title: string; rows: ChangeRow[]; other: string; missing: string }) {
  const [all, setAll] = useState(false);
  const n = rows.length;
  return (
    <div class="sr-sum-group">
      <div class="sr-sum-group-head sr-ui">{title}<span class="sr-sum-group-n"> · {n}</span></div>
      {(n >= CHANGES_FOLD && !all ? rows.slice(0, 1) : rows).map((x, i) => <ChangeRowView key={i} x={x} other={other} missing={missing} />)}
      {n >= CHANGES_FOLD && (
        <button class="sr-disclose" onClick={() => setAll(!all)} aria-expanded={all}>
          {all ? '▾ Show less' : `▸ Show all ${n}`}
        </button>
      )}
      {!n && <div class="sr-empty">None.</div>}
    </div>
  );
}

/** Check B: for a bill one chamber passed and the other changed before the final votes, what the first-passed
 *  text had that the final text does not, and the reverse (Dan, 30 Sep 2026: after "What it does"). */
export function VersionChanges({ c, kind }: { c: Changes; kind: string }) {
  const word = kind === 'bill' ? 'bill' : 'resolution';
  const first = c.first.chamber, second = otherChamber(first);
  const link = (label: string, href: string | null) => (href ? <a href={href} rel="noopener">{label}</a> : label);
  return (
    <div class="sr-sec sr-changes">
      <div class="sr-kicker">{CHANGES.title(first)}</div>
      <div class="sr-crs-about sr-ui">
        Compares the {word} {link(`as passed by the ${first}`, c.first.href)}
        {c.first.date ? `, ${fmtLong(c.first.date)}` : ''}, with {link('the final text', c.final.href)} passed by both
        chambers{c.final.date ? `, ${fmtLong(c.final.date)}` : ''}. The {second} changed it in between.
        {c.kept !== null && ` About ${Math.round(c.kept * 100)}% of the wording the ${first} passed stands in the final text.`}
        {' '}Sections in both texts are not listed, even where their wording changed.
      </div>
      {c.replaced && (
        <p class="sr-sum-lead">
          Less than a tenth of the final text’s wording was in the text the {first} passed:{' '}
          {c.added_count.toLocaleString('en-US')} of its {c.added_count === 1 ? 'sections was' : 'sections were'} not in it.
        </p>
      )}
      <ChangeList title={CHANGES.removed} rows={c.removed} other="final text" missing={CHANGES.notCarried} />
      {!c.replaced && <ChangeList title={CHANGES.added(first)} rows={c.added} other={`text the ${first} passed`}
        missing={CHANGES.newInFinal} />}
      <div class="sr-crs-credit sr-ui">
        {c.replaced || c.removed.length + c.added.length === 0
          ? 'Code compared the two texts word for word; no AI model was used.'
          : 'Code compared the two texts word for word. Where a section’s wording is not in the other text, Claude ' +
            'Sonnet 5.5, an AI model, judged whether a section of the other text carries the same provision in other ' +
            'words, quoting it; a section it could not settle is not listed.'}
        {c.approved_by === 'checks' && c.approved
          ? ` Written ${fmtLong(c.approved)} and published after the automatic checks, without a person reading it first.`
          : c.approved ? ` Approved for publication ${fmtLong(c.approved)}.` : ''}
      </div>
    </div>
  );
}

function ItemBody({ doc, onClose, procOpen, setProcOpen, titlesOpen, setTitlesOpen }: {
  doc: ItemDoc; onClose: () => void;
  procOpen: boolean; setProcOpen: (b: boolean) => void; titlesOpen: boolean; setTitlesOpen: (b: boolean) => void;
}) {
  const finalDate = [doc.chambers.house.date, doc.chambers.senate.date].filter(Boolean).sort().pop() ?? null;
  const outcome = outcomeWords(doc.outcome, doc.kind);
  const outcomeDef = defFor(outcome) || (doc.kind === 'concurrent_resolution' ? JARGON['concurrent resolution'] : '');
  const [share, setShare] = useState(false);
  useEffect(() => setShare(false), [doc.id]);

  return (
    <>
      <div class="sr-modal-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* a batch's number already says what it is: "Nominations en bloc (S.Res. 377)" */}
          <div class="sr-num">{doc.kind === 'nomination_batch' ? doc.number : `${doc.number} · ${kindLabel(doc)}`}</div>
          <h3>{doc.title || doc.official_title || doc.number}</h3>
          <div class="sr-modal-meta">
            <span class={'tag tag-outline' + (outcomeDef ? ' sr-dotted' : '')} data-def={outcomeDef || undefined}>{outcome}</span>
            <span class="sr-dates">
              {doc.introduced && `Introduced ${fmtLong(doc.introduced)} · `}
              {finalDate ? `Final vote ${fmtLong(finalDate)}` : 'No final vote yet'}
            </span>
          </div>
        </div>
        <ShareButton open={share} onToggle={() => setShare(!share)} controls="sr-share-item" />
        <button class="btn btn-ghost sr-close" onClick={onClose} aria-label="Close">×</button>
      </div>
      {share && <SharePanel id="sr-share-item" what={itemShare(doc)} />}

      <div class="sr-sec">
        <div class="sr-kicker">Vote results</div>
        <table class="table">
          <thead><tr><th>Chamber</th><th>Date</th><th>Yea</th><th>Nay</th><th>Present</th><th>Not voting</th><th>Result</th></tr></thead>
          <tbody>
            {(['house', 'senate'] as const).map((ch) => {
              const c = doc.chambers[ch], name = ch === 'house' ? 'House' : 'Senate';
              if (c.status === 'na') return null;
              if (c.status === 'pending')
                return <tr key={ch}><td>{name}</td><td>—</td><td>—</td><td>—</td><td>—</td><td>—</td><td>Not yet considered</td></tr>;
              if (c.status === 'voice' || !c.vote) {
                const how = (c.how && NO_ROLL_CALL[c.how]) || NO_ROLL_CALL_ANY;
                return <tr key={ch}><td>{name}</td><td>{c.date ? fmtLong(c.date) : '—'}</td><td>—</td><td>—</td><td>—</td><td>—</td>
                  <td><span class="sr-dotted" data-def={how.def}>{how.result}</span></td></tr>;
              }
              const v = c.vote, p = c.override ? c.passage : null;
              const row = (key: string, label: string, day: string | null,
                           x: { yea: number | null; nay: number | null; present: number | null; not_voting: number | null;
                                result: string | null; how?: string | null }) => {
                // a passage without a roll call (H.R. 131's, by voice vote) says how, as a voice-vote row does
                const how = x.result == null ? (x.how && NO_ROLL_CALL[x.how]) || NO_ROLL_CALL_ANY : null;
                return <tr key={key}><td>{label}</td><td>{day ? fmtLong(day) : '—'}</td><td>{num(x.yea)}</td><td>{num(x.nay)}</td>
                  <td>{num(x.present)}</td><td>{num(x.not_voting)}</td>
                  <td>{how ? <span class="sr-dotted" data-def={how.def}>{how.result}</span> : x.result}</td></tr>;
              };
              // A veto override is the chamber's last vote, but not its vote on passing the bill: both shown.
              if (c.override)
                return [p ? row(ch + '-passage', `${name} · passage`, p.date, p) : null,
                        row(ch, `${name} · veto override`, c.date, v)];
              return row(ch, isTrialVote(v.question) ? `${name} · trial verdict` : name, c.date, v);
            })}
          </tbody>
        </table>
        {(['house', 'senate'] as const).map((ch) => {
          const c = doc.chambers[ch];
          // A chamber that passed it without a roll call: the record's own words, as it gives them.
          const words = c.status === 'voice' ? c.words : c.override && c.passage?.result == null ? c.passage?.words : null;
          return words
            ? <div key={ch} class="sr-line">{ch === 'house' ? 'House' : 'Senate'}, in the record’s words: “{words}”</div>
            : null;
        })}
        {(['house', 'senate'] as const).some((ch) => isTrialVote(doc.chambers[ch].vote?.question)) && (
          <div class="sr-line">{TRIAL_NOTE}</div>
        )}
        {doc.vice_president_tie_breaker && (
          <div class="sr-line">Vice President: voted {doc.vice_president_tie_breaker.replace(/^Tie-breaker:\s*/i, '')} to break a tie</div>
        )}
        <div class="sr-line">President: {presidentLine(doc)}</div>
      </div>

      <ItemSummaries doc={doc} />

      <ItemMore doc={doc} procOpen={procOpen} setProcOpen={setProcOpen} titlesOpen={titlesOpen} setTitlesOpen={setTitlesOpen} />
    </>
  );
}

/** The item's nominees, or its summaries: ours once approved, the CRS's, or a note that none is
 *  published yet. On a phone they follow the vote results, before everyone's marks (Dan, 29 Sep
 *  2026: "Summary first"). */
export function ItemSummaries({ doc }: { doc: ItemDoc }) {
  const nomination = doc.kind === 'nomination' || doc.kind === 'nomination_batch';
  const finalDate = [doc.chambers.house.date, doc.chambers.senate.date].filter(Boolean).sort().pop() ?? null;
  return (
    <>
      {doc.nominees && doc.nominees.length > 0 && (
        <div class="sr-sec">
          <div class="sr-kicker">{doc.nominees.length === 1 ? 'Nominee' : `${doc.nominees.length} nominees, confirmed in one vote`}</div>
          {doc.nominees.map((n) => (
            <div key={n.pn} class="sr-nominee"><span class="sr-ui">{n.pn}</span><span>{n.title}</span></div>
          ))}
        </div>
      )}

      {!nomination && doc.summary && <AiSummary s={doc.summary} kind={doc.kind} chamber={doc.changes?.first.chamber} />}

      {!nomination && doc.changes && <VersionChanges c={doc.changes} kind={doc.kind} />}

      {!nomination && doc.crs_summary && <CrsSummary crs={doc.crs_summary} finalDate={finalDate} kind={doc.kind} />}

      {!nomination && !doc.crs_summary && !doc.summary && (
        <div class="sr-sec">
          <div class="sr-pending-box">
            <div class="sr-kicker">Summary not published yet</div>
            <p>The votes above are the official record and are current. The plain-language summary of this
              item’s text has not been published yet; the full text is linked below in the meantime.</p>
          </div>
        </div>
      )}
    </>
  );
}

/** The rest of the item: its titles, amendments, other roll calls, sources and the error report. */
export function ItemMore({ doc, procOpen, setProcOpen, titlesOpen, setTitlesOpen }: {
  doc: ItemDoc; procOpen: boolean; setProcOpen: (b: boolean) => void; titlesOpen: boolean; setTitlesOpen: (b: boolean) => void;
}) {
  const nomination = doc.kind === 'nomination' || doc.kind === 'nomination_batch';
  // Amendment votes are listed in their own section; one with no amendment record behind
  // it stays here, so no recorded vote on the item is dropped.
  const amendmentSources = new Set(doc.amendments.map((a) => a.source));
  const others = doc.other_votes.filter((o) => o.kind !== 'amendment' || !amendmentSources.has(o.source));
  const original = doc.titles[0];
  return (
    <>
      {original && (
        <div class="sr-sec">
          <div class="sr-kicker">{doc.titles.length === 1 ? 'Title in the record' : 'Titles in the record'}</div>
          <div class="sr-title-row">
            <div class="sr-ui">{original.type} · the original</div>
            <div>{original.title}</div>
          </div>
          {doc.titles.length > 1 && (
            <>
              <button class="sr-disclose" style={{ marginTop: '12px' }} onClick={() => setTitlesOpen(!titlesOpen)}
                      aria-expanded={titlesOpen}>
                {(titlesOpen ? '▾ ' : '▸ ') + `${doc.titles.length - 1} more title${doc.titles.length > 2 ? 's' : ''} in the record`}
              </button>
              {titlesOpen && (
                <div class="sr-titles">
                  {doc.titles.slice(1).map((t, i) => (
                    <div key={i} class="sr-title-row"><div class="sr-ui">{t.type}</div><div>{t.title}</div></div>
                  ))}
                </div>
              )}
            </>
          )}
          {doc.title && doc.title_source && (
            <div class="sr-line sr-muted">The heading above is the record’s “{doc.title_source.replace(/\(s\)/, '').replace(/^Popular Titles$/, 'Popular Title')}”.</div>
          )}
        </div>
      )}

      {!nomination && (
        <div class="sr-sec">
          <div class="sr-kicker">Amendments with a recorded vote</div>
          {doc.amendments.map((a) => (
            <div key={a.number + a.date} class="sr-amd">
              <div class="sr-amd-top">
                <span>{a.number}{a.sponsor ? ' · ' + a.sponsor : ''}</span>
                <span>{a.result} · {tally(a)}</span>
              </div>
              {a.purpose && <div class="sr-amd-purpose">{a.purpose}</div>}
            </div>
          ))}
          {!doc.amendments.length && <div class="sr-empty">No amendments received a recorded vote.</div>}
        </div>
      )}

      <div class="sr-sec">
        <button class="sr-disclose" onClick={() => setProcOpen(!procOpen)} aria-expanded={procOpen}>
          {(procOpen ? '▾ ' : '▸ ') + `Other recorded votes (${others.length})`}
        </button>
        {procOpen && (
          <div style={{ marginTop: '12px' }}>
            {others.map((o, i) => {
              const def = defFor(o.question);
              return (
                <div key={i} class="sr-proc">
                  <span class={def ? 'sr-dotted' : ''} data-def={def || undefined}>{o.question}</span>
                  <span class="sr-ui">{o.chamber === 'house' ? 'House' : 'Senate'} · {fmtLong(o.date)} · {o.result} · {tally(o)}</span>
                </div>
              );
            })}
            {!others.length && <div class="sr-empty">No other roll calls were recorded on this item.</div>}
          </div>
        )}
      </div>

      <div class="sr-sec">
        <div class="sr-kicker">Sources</div>
        <div class="sr-sources">
          {doc.sources.congress_gov && <div><a href={doc.sources.congress_gov} rel="noopener">Congress.gov page for {doc.number}</a></div>}
          {doc.sources.text && <div><a href={doc.sources.text} rel="noopener">Full text</a></div>}
          {(['house', 'senate'] as const).map((ch) => doc.chambers[ch].vote && (
            <div key={ch}><a href={doc.chambers[ch].vote!.source} rel="noopener">
              {ch === 'house' ? 'House' : 'Senate'} roll call, {fmtLong(doc.chambers[ch].date!)}</a></div>
          ))}
        </div>
        <div class="sr-disclosure">
          Every figure on this item is from the official record.{' '}
          {doc.summary && <>The summary was written automatically from the official text, and every line links to the
            section it came from.{' '}</>}
          <button class="sr-linkbtn" onClick={() => navigate('/report?item=' + encodeURIComponent(doc.id))}>Report an error</button>
          {' '}· Shareable link: <span>votesonrecord.org/item/{doc.id}</span>
        </div>
      </div>
    </>
  );
}
